import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Stack,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Tooltip,
  TablePagination,
  Chip,
  Portal,
  ClickAwayListener,
} from "@mui/material";
import {
  Search,
  Timer,
  Play,
  Calendar as CalendarIcon,
  X,
  Hash,
  Headphones,
  Filter,
  FilterX,
} from "lucide-react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { motion } from "framer-motion";
import axiosInstance from "../../../../services/axiosInstance";
import SideNavbar from "../../../../components/sideNavbar";
import Header from "../../../../components/header";
import apiEndPointsConfig from "../../../../services/apiEndPoints";
import EmptyState from "../components/EmptyState";

const COLORS = {
  primary: "#6366F1",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  cyan: "#06B6D4",
};

const CHART_COLORS = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

// Animated KPI Card
const KPICard = ({ title, value, icon: Icon, color, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid #E2E8F0",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.06)",
        },
      }}
    >
      {loading ? (
        <Stack spacing={1}>
          <Skeleton width="60%" height={20} />
          <Skeleton width="40%" height={32} />
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${color}15`,
              color: color,
              display: "flex",
            }}
          >
            <Icon size={20} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1E293B", lineHeight: 1 }}
            >
              {value}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#64748B",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              {title}
            </Typography>
          </Box>
        </Stack>
      )}
    </Paper>
  </motion.div>
);

// Chart Paper Component
const ChartPaper = ({ title, children, loading }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, borderRadius: 3, border: "1px solid #E2E8F0" }}
  >
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, mb: 2, color: "#475569" }}
    >
      {title}
    </Typography>
    {loading ? (
      <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 1 }} />
    ) : (
      children
    )}
  </Paper>
);

const CDRReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [dispositionFilter, setDispositionFilter] = useState("All");

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllCallLogs}/${userId}?limit=100000&export=true`
      );
      setData([...(res?.data?.data || [])].reverse());
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unique dispositions
  const filterOptions = useMemo(() => {
    const dispositions = [
      ...new Set(data.map((i) => i.payload?.sub_disposition)),
    ].filter(Boolean);
    return { dispositions };
  }, [data]);

  // Filter data
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const p = item.payload || {};

      // Search filter
      const matchesSearch =
        !searchQuery ||
        p.mobile?.includes(searchQuery) ||
        p.user?.toLowerCase().includes(searchQuery.toLowerCase());

      // Disposition filter
      const matchesDisposition =
        dispositionFilter === "All" || p.sub_disposition === dispositionFilter;

      // Date range filter
      let matchesDate = true;
      if (selectedDateRange && selectedDateRange[0] && selectedDateRange[1]) {
        const callDate = new Date(p.callDate);
        const startDate = new Date(selectedDateRange[0]);
        const endDate = new Date(selectedDateRange[1]);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        matchesDate = callDate >= startDate && callDate <= endDate;
      }

      return matchesSearch && matchesDisposition && matchesDate;
    });
  }, [data, searchQuery, dispositionFilter, selectedDateRange]);

  // Analytics
  const analytics = useMemo(() => {
    const total = filteredData.length;
    let totalDur = 0;
    const hourMap = {};
    const statusMap = {};

    filteredData.forEach((item) => {
      const p = item.payload || {};
      totalDur += Number(p.timeDuration || 0);
      const hour = p.calls_time?.split(":")[0] || "00";
      hourMap[hour] = (hourMap[hour] || 0) + 1;
      const status = p.sub_disposition || "Other";
      statusMap[status] = (statusMap[status] || 0) + 1;
    });

    const hourlyData = Object.keys(hourMap)
      .sort()
      .map((h) => ({ hour: `${h}:00`, calls: hourMap[h] }));

    const statusData = Object.entries(statusMap)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 12) + "..." : name,
        fullName: name,
        value,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    return {
      total,
      avg: total > 0 ? (totalDur / total).toFixed(0) : 0,
      min: (totalDur / 60).toFixed(1),
      hourlyData,
      statusData,
    };
  }, [filteredData]);

  const clearAllFilters = () => {
    setDispositionFilter("All");
    setSelectedDateRange(null);
    setSearchQuery("");
    setPage(0);
  };

  const hasActiveFilters =
    dispositionFilter !== "All" || selectedDateRange || searchQuery;

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <SideNavbar />
      <Box sx={{ ml: { xs: 0, md: "280px" } }}>
        {/* <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <Header title="Call Intelligence Audit" />
        </Box> */}


        <Box component="main" sx={{ p: { xs: 2, md: 3 } }}>
          {/* TOP TOOLBAR */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 800, color: "#1E293B" }}
              >
                CDR Analytics
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Detailed communication audit logs
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              {/* Active Filters Chips */}
              {selectedDateRange && (
                <Chip
                  size="small"
                  label={`${selectedDateRange[0].toLocaleDateString("en-GB")} - ${selectedDateRange[1].toLocaleDateString("en-GB")}`}
                  onDelete={() => setSelectedDateRange(null)}
                  sx={{
                    bgcolor: "#EEF2FF",
                    color: COLORS.primary,
                    fontWeight: 600,
                    borderRadius: 1,
                  }}
                />
              )}
              {dispositionFilter !== "All" && (
                <Chip
                  size="small"
                  label={`Disp: ${dispositionFilter.length > 15 ? dispositionFilter.substring(0, 12) + "..." : dispositionFilter}`}
                  onDelete={() => setDispositionFilter("All")}
                  sx={{
                    bgcolor: "#FEF3C7",
                    color: COLORS.warning,
                    fontWeight: 600,
                    borderRadius: 1,
                  }}
                />
              )}

              <TextField
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ bgcolor: "white", borderRadius: 1, width: 180 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={16} color="#6366F1" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                select
                size="small"
                value={dispositionFilter}
                onChange={(e) => {
                  setDispositionFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 140, bgcolor: "white", borderRadius: 2 }}
              >
                <MenuItem value="All">All Dispositions</MenuItem>
                {filterOptions.dispositions.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d.length > 20 ? d.substring(0, 18) + "..." : d}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                startIcon={<CalendarMonthIcon />}
                onClick={() => setCalendarVisible(!calendarVisible)}
                sx={{
                  bgcolor: COLORS.primary,
                  textTransform: "none",
                  borderRadius: 1,
                  px: 2,
                  borderRadius: 2,
                }}
              >
                Date
              </Button>

              {hasActiveFilters && (
                <Tooltip title="Clear all filters">
                  <IconButton
                    onClick={clearAllFilters}
                    sx={{ color: COLORS.danger }}
                  >
                    <FilterX size={20} />
                  </IconButton>
                </Tooltip>
              )}

              {/* Calendar Portal */}
              {calendarVisible && (
                <Portal>
                  <ClickAwayListener
                    onClickAway={() => setCalendarVisible(false)}
                  >
                    <Box
                      sx={{
                        position: "fixed",
                        top: "120px",
                        right: "40px",
                        zIndex: 1300,
                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                        borderRadius: 1,
                        bgcolor: "#fff",
                        overflow: "hidden",
                      }}
                    >
                      <Calendar
                        selectRange
                        onChange={(r) => {
                          setSelectedDateRange(r);
                          setCalendarVisible(false);
                          setPage(0);
                        }}
                        value={selectedDateRange}
                        maxDate={new Date()}
                      />
                    </Box>
                  </ClickAwayListener>
                </Portal>
              )}
            </Stack>
          </Stack>

          {/* KPI CARDS */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Total Calls"
                value={analytics.total}
                icon={Hash}
                color={COLORS.primary}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Avg Duration"
                value={`${analytics.avg}s`}
                icon={Timer}
                color={COLORS.success}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KPICard
                title="Total Minutes"
                value={`${analytics.min}m`}
                icon={Headphones}
                color={COLORS.warning}
                loading={loading}
              />
            </Grid>
          </Grid>

          {/* CHARTS */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={7}>
              <ChartPaper title="Hourly Call Density" loading={loading}>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={analytics.hourlyData}
                      margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorCallsCDR"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={COLORS.primary}
                            stopOpacity={0.4}
                          />
                          <stop
                            offset="95%"
                            stopColor={COLORS.primary}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#E2E8F0"
                      />
                      <XAxis
                        dataKey="hour"
                        tick={{ fontSize: 11, fill: "#94A3B8" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#94A3B8" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <RechartsTooltip
                        formatter={(value) => [`${value} calls`, "Calls"]}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "none",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="calls"
                        stroke={COLORS.primary}
                        strokeWidth={2}
                        fill="url(#colorCallsCDR)"
                        dot={{ r: 3, fill: COLORS.primary }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPaper>
            </Grid>
            <Grid item xs={12} lg={5}>
              <ChartPaper title="Outcome Mix" loading={loading}>
                <Box sx={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={analytics.statusData}
                      layout="vertical"
                      margin={{ left: 10, right: 10 }}
                    >
                      <XAxis
                        type="number"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: "#94A3B8" }}
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        fontSize={10}
                        width={80}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#475569", fontWeight: 600 }}
                      />
                      <RechartsTooltip
                        cursor={{ fill: "#F1F5F9" }}
                        formatter={(value, name, props) => [
                          `${value} calls`,
                          props.payload.fullName,
                        ]}
                        contentStyle={{
                          borderRadius: "8px",
                          border: "none",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={16}>
                        {analytics.statusData.map((_, i) => (
                          <Cell
                            key={i}
                            fill={CHART_COLORS[i % CHART_COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </ChartPaper>
            </Grid>
          </Grid>

          {/* TABLE SECTION */}
          {!loading && filteredData.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                borderRadius: 1,
                border: "1px solid #E2E8F0",
                minHeight: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmptyState
                title="No Call Records Found"
                subtitle="No calls match your current filters. Try adjusting the date range or disposition filter."
                icon="call"
                onRefresh={fetchData}
              />
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #E2E8F0",
                overflow: "hidden",
              }}
            >
              <TableContainer sx={{ maxHeight: 450 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        "Timestamp",
                        "Agent",
                        "Contact",
                        "Duration",
                        "Outcome",
                        "Recording",
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            bgcolor: "#F8FAFC",
                            fontWeight: 700,
                            color: "#64748B",
                            py: 1.5,
                            fontSize: 11,
                            textTransform: "uppercase",
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading
                      ? [...Array(6)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={6}>
                            <Skeleton height={40} />
                          </TableCell>
                        </TableRow>
                      ))
                      : filteredData
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row) => <CDRRow key={row._id} row={row} />)}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// --- SUB COMPONENTS ---

const CDRRow = memo(({ row }) => {
  const p = row.payload || {};
  const isConnected = p.status !== "Did Not Connect";
  const recordingUrl = row.payload.location || p.blobUrl;

  return (
    <TableRow hover sx={{ "&:hover": { bgcolor: "#F8FAFC" } }}>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarIcon size={14} color="#6366F1" />
          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, display: "block", color: "#1E293B" }}
            >
              {p.callDate}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94A3B8" }}>
              {p.calls_time}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 24,
              height: 24,
              fontSize: 10,
              bgcolor: "#EEF2FF",
              color: "#6366F1",
            }}
          >
            {p.user?.[0]}
          </Avatar>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {p.user}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: "#475569" }}
        >
          {p.mobile}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#64748B" }}
        >
          {p.timeDuration}s
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={p.sub_disposition || "No Status"}
          sx={{
            height: 22,
            fontSize: "0.65rem",
            fontWeight: 700,
            bgcolor: isConnected ? "#DCFCE7" : "#FEE2E2",
            color: isConnected ? "#15803D" : "#B91C1C",
          }}
        />
      </TableCell>
      <TableCell align="right">
        {recordingUrl ? (
          <Tooltip title="Listen">
            <IconButton
              href={recordingUrl}
              target="_blank"
              size="small"
              sx={{
                bgcolor: COLORS.primary,
                color: "white",
                width: 26,
                height: 26,
                "&:hover": { bgcolor: "#4F46E5" },
              }}
            >
              <Play size={12} fill="white" />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="caption" sx={{ color: "#CBD5E1" }}>
            —
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
});

export default CDRReport;
